import * as React from "react";
import {useEffect} from "react";
import {RequestActions, RequestDTO} from "@/api/data-contracts";
import {requestApi} from "@/boxhub-api";
import {useSnackbarEx} from "@/lib/snackbarex";
import {getI18n, useTranslation} from "react-i18next";
import {CommonI18N, RequestI18N} from "@/translations";
import {formatInteger} from "@/lib/utils";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {Constants} from "@/api/constants";
import Alert from "@mui/material/Alert";

interface EditAdditionalInformationDialogProps {
    open: boolean;
    onClose: () => void;
    model: RequestDTO;
    onSuccess: (updated: RequestDTO) => void;
}

/**
 * Dialog for editing the additional information of a request.
 * Analogous to ExtendDatesDialog — manages its own local string state for the single field.
 * On save, calls the updateAdditionalInformation API, shows a success snackbar,
 * and notifies the parent via onSuccess.
 *
 * The Save button is only enabled when the trimmed input value differs from the trimmed
 * original value (dirty check). This ensures that purely cosmetic whitespace edits or
 * re-opening the dialog without changes cannot trigger a superfluous API call.
 *
 * @param open - Whether the dialog is visible.
 * @param onClose - Callback to close the dialog without saving.
 * @param model - The current request, used to initialise the text field.
 * @param onSuccess - Callback invoked with the updated request after a successful save.
 */
const EditAdditionalInformationDialog: React.FC<EditAdditionalInformationDialogProps> = ({
                                                                                             open,
                                                                                             onClose,
                                                                                             model,
                                                                                             onSuccess
                                                                                         }) => {
    const {t} = useTranslation();
    const locale = getI18n().language;
    const {showMessage} = useSnackbarEx();
    const [value, setValue] = React.useState<string>(model.additionalInformation ?? "");
    const [processing, setProcessing] = React.useState(false);

    // Reset the local value to the current model data each time the dialog is opened,
    // so that a previously saved value is reflected correctly on re-open.
    useEffect(() => {
        if (open) {
            setValue(model.additionalInformation ?? "");
        }
    }, [open, model.additionalInformation]);

    const trimmed = value.trim();
    const originalNormalized = (model.additionalInformation ?? "").trim();
    const isDirty = trimmed !== originalNormalized;
    const isTooLong = value.length > Constants.REQUEST_ADDITIONAL_INFORMATION_LENGTH_MAX;

    const handleSave = async () => {
        if (!model.id || isTooLong) return;
        setProcessing(true);
        try {
            const payload = {additionalInformation: trimmed.length === 0 ? null : trimmed} as {
                additionalInformation?: string
            };
            const response = await requestApi.updateAdditionalInformation(model.id, payload);
            showMessage({
                summary: t(RequestI18N.details.message.editAdditionalInformationSuccess),
                severity: "success"
            });
            onSuccess(response.data);
            onClose();
        } catch (error: unknown) {
            showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
        } finally {
            setProcessing(false);
        }
    };

    return (
            <ConfirmationDialog
                    open={open}
                    onClose={onClose}
                    onConfirm={handleSave}
                    title={t(RequestI18N.details.dialog.editAdditionalInformationTitle)}
                    message=""
                    confirmButtonText={t(RequestI18N.details.action.editAdditionalInformationSave)}
                    cancelButtonText={t(CommonI18N.action.cancel)}
                    confirmDisabled={isTooLong || processing || !isDirty}
                    autoFocusConfirm={false}
                    maxWidth="sm"
                    fullWidth
            >
                <div style={{marginTop: 12}}>
                    {model.allowedActions?.includes(RequestActions.UPDATE_ADDITIONAL_INFORMATION) && (
                            <Alert severity="info" sx={{mb: 2}}>
                                {t(RequestI18N.details.message.editAdditionalInformationEmailHint)}
                            </Alert>
                    )}
                    <StringInputField
                            multiline
                            minRows={4}
                            label={t(RequestI18N.details.field.additionalInformationBuyer)}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            maxChars={Constants.REQUEST_ADDITIONAL_INFORMATION_LENGTH_MAX}
                            emphasized
                            error={
                                isTooLong
                                        ? {
                                            message: t(RequestI18N.form.validation.additionalInformationMaxLength, {
                                                max: formatInteger(Constants.REQUEST_ADDITIONAL_INFORMATION_LENGTH_MAX, locale)
                                            })
                                        }
                                        : undefined
                            }
                    />
                </div>
            </ConfirmationDialog>
    );
};

export default EditAdditionalInformationDialog;
